/**
 * JS SDK de Meta para el flujo de **Embedded Signup** de WhatsApp (el camino oficial para que un
 * negocio conecte su WABA y su número desde el dashboard, sin pegar credenciales).
 *
 * Cómo funciona (ver `docs/PRODUCCION_META.md` y `docs/ONBOARDING_OAUTH.md`):
 *  1. `FB.init` con el App ID y la versión de la Graph API.
 *  2. `FB.login` con `config_id` (la configuración de Facebook Login for Business) → Meta abre su
 *     ventana: el cliente elige su portfolio, su WABA y su número.
 *  3. Al terminar llegan DOS cosas por caminos distintos: el **`code`** por el callback del login y
 *     el **`waba_id` + `phone_number_id`** por un `postMessage` (`WA_EMBEDDED_SIGNUP`). Pueden llegar
 *     en cualquier orden, así que se esperan las dos.
 *  4. El `code` vive **30 segundos**: el panel lo manda al backend de inmediato
 *     (`POST /api/integrations/oauth/:tenantId/meta/whatsapp/complete`), que canjea el code, suscribe
 *     la WABA y guarda las credenciales cifradas.
 *
 * `parseEmbeddedSignupMessage` es puro (no toca el DOM) a propósito: así el smoke lo puede probar
 * sin navegador.
 */

import { env } from './env';

/** URL oficial del SDK (la versión no va aquí: va en `FB.init`). */
export const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

/**
 * Versión de la Graph API que usa el SDK en el navegador. **Tiene que coincidir con
 * `GRAPH_VERSION` del backend** (`backend/src/modules/channels/meta-graph.client.ts`): Meta retira
 * cada versión ~2 años después de salir, así que si se sube allí hay que subirla aquí también.
 */
export const META_GRAPH_VERSION = 'v25.0';

/** Origen desde el que Meta manda los mensajes del flujo. */
const FACEBOOK_ORIGIN = 'https://www.facebook.com';

const EMBEDDED_SIGNUP_MESSAGE_TYPE = 'WA_EMBEDDED_SIGNUP';

export interface EmbeddedSignupMessage {
  event: 'FINISH' | 'CANCEL';
  wabaId: string | null;
  phoneNumberId: string | null;
}

/** ¿Están las dos piezas de configuración? Si no, el panel esconde el botón y ofrece la carga manual. */
export function isEmbeddedSignupConfigured(appId: string, configId: string): boolean {
  return appId.trim().length > 0 && configId.trim().length > 0;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value) as unknown);
    } catch {
      return {};
    }
  }
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/**
 * Interpreta un mensaje de la ventana de Embedded Signup. Devuelve `null` si no es del flujo (un
 * mensaje de otra cosa) y NO lanza nunca: un `postMessage` ajeno no puede romper el panel.
 *
 * Forma documentada: `{ type: 'WA_EMBEDDED_SIGNUP', event: 'FINISH'|'CANCEL', data: { waba_id,
 * phone_number_id } }`. `data` puede venir como objeto o como JSON en texto (según la versión del
 * flujo), así que se aceptan las dos.
 */
export function parseEmbeddedSignupMessage(
  data: unknown,
  origin?: string,
): EmbeddedSignupMessage | null {
  if (origin && origin !== FACEBOOK_ORIGIN && !origin.endsWith('.facebook.com')) return null;

  const payload = asRecord(data);
  if (payload.type !== EMBEDDED_SIGNUP_MESSAGE_TYPE) return null;

  const event = payload.event === 'FINISH' ? 'FINISH' : payload.event === 'CANCEL' ? 'CANCEL' : null;
  if (!event) return null;

  const detail = asRecord(payload.data);
  return {
    event,
    wabaId: typeof detail.waba_id === 'string' ? detail.waba_id : null,
    phoneNumberId: typeof detail.phone_number_id === 'string' ? detail.phone_number_id : null,
  };
}

interface FacebookLoginResponse {
  status?: string;
  authResponse?: { code?: string } | null;
}

interface FacebookSdk {
  init(options: {
    appId: string;
    autoLogAppEvents?: boolean;
    xfbml?: boolean;
    version: string;
  }): void;
  login(callback: (response: FacebookLoginResponse) => void, options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
  }
}

let sdkPromise: Promise<FacebookSdk> | null = null;

/** Carga el SDK una sola vez (inyecta el `<script>` oficial) y lo devuelve listo para `init`. */
export function loadFacebookSdk(): Promise<FacebookSdk> {
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<FacebookSdk>((resolve, reject) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      reject(new Error('El SDK de Facebook solo se puede cargar en el navegador'));
      return;
    }
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    const script = document.createElement('script');
    script.src = FACEBOOK_SDK_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (window.FB) resolve(window.FB);
      else reject(new Error('El SDK de Facebook cargó pero no expuso `FB`'));
    };
    script.onerror = () => {
      sdkPromise = null; // permite reintentar
      reject(
        new Error(
          'No se pudo cargar el SDK de Facebook (revisa la conexión, un bloqueador de anuncios o la CSP)',
        ),
      );
    };
    document.head.appendChild(script);
  });

  return sdkPromise;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('El flujo de Embedded Signup tardó demasiado (¿se cerró la ventana?)')),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

export interface EmbeddedSignupSession {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

export interface EmbeddedSignupConfig {
  appId: string;
  configId: string;
  /** Versión de la Graph API para `FB.init` (por defecto `META_GRAPH_VERSION`). */
  graphVersion?: string;
  /** Cuánto esperar al cliente antes de rendirse (por defecto 5 minutos). */
  timeoutMs?: number;
}

/**
 * Abre la ventana de Embedded Signup y devuelve el `code` + el WABA + el número.
 * Lanza `Error` con un mensaje en español si el cliente cancela, si falta algún dato o si se agota
 * el tiempo. El `code` caduca en 30 s: el llamador debe mandarlo al backend de inmediato.
 */
export async function launchEmbeddedSignup(
  config: EmbeddedSignupConfig,
): Promise<EmbeddedSignupSession> {
  const sdk = await loadFacebookSdk();
  sdk.init({
    appId: config.appId,
    autoLogAppEvents: true,
    xfbml: false,
    version: config.graphVersion ?? META_GRAPH_VERSION,
  });

  let quitarListener: () => void = () => {};

  // El `waba_id` y el `phone_number_id` llegan por aquí (y el `code` por el callback del login).
  const mensaje = new Promise<EmbeddedSignupMessage>((resolve) => {
    const onMessage = (event: MessageEvent) => {
      const parsed = parseEmbeddedSignupMessage(event.data, event.origin);
      if (parsed) resolve(parsed);
    };
    window.addEventListener('message', onMessage);
    quitarListener = () => window.removeEventListener('message', onMessage);
  });

  const codigo = new Promise<string>((resolve, reject) => {
    sdk.login(
      (response) => {
        const code = response.authResponse?.code;
        if (code) resolve(code);
        else reject(new Error('Se canceló la conexión con Facebook o no se autorizó el acceso.'));
      },
      {
        config_id: config.configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {} },
      },
    );
  });

  try {
    const [code, evento] = await withTimeout(
      Promise.all([codigo, mensaje]),
      config.timeoutMs ?? 5 * 60 * 1000,
    );

    if (evento.event === 'CANCEL' || !evento.wabaId || !evento.phoneNumberId) {
      throw new Error(
        'El flujo de Embedded Signup se canceló antes de terminar (faltan el WABA o el número).',
      );
    }

    return { code, wabaId: evento.wabaId, phoneNumberId: evento.phoneNumberId };
  } finally {
    quitarListener();
  }
}

/** Atajo con la configuración del entorno (`VITE_META_APP_ID` + `VITE_META_ES_CONFIG_ID`). */
export function launchEmbeddedSignupFromEnv(): Promise<EmbeddedSignupSession> {
  if (!isEmbeddedSignupConfigured(env.metaAppId, env.metaEmbeddedSignupConfigId)) {
    return Promise.reject(
      new Error(
        'Embedded Signup no está configurado (faltan VITE_META_APP_ID y VITE_META_ES_CONFIG_ID)',
      ),
    );
  }
  return launchEmbeddedSignup({ appId: env.metaAppId, configId: env.metaEmbeddedSignupConfigId });
}
